import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import HttpStatus from '../../../../../enums/httpStatus';
import { mobileOrderStatusApi } from '../../../../../services/requests';

type Props = {
  closeModal: () => void;
  reqGetAllOrderStatuses: () => void;
};

const CreateOrderStatus = ({ closeModal, reqGetAllOrderStatuses }: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [sigla, setSigla] = useState('');
  const [descricao, setDescricao] = useState('');
  const [isUltimo, setIsUltimo] = useState<'s' | 'n'>('n');
  const [cor, setCor] = useState('');
  const [legenda, setLegenda] = useState('');
  const [exibeListaPedido, setExibeListaPedido] = useState<'s' | 'n'>('n');
  const [peso, setPeso] = useState('0');

  const reqCreateOrderStatus = async () => {
    setIsLoading(true);

    const source = mobileOrderStatusApi.axios.CancelToken.source();

    try {
      const res = await mobileOrderStatusApi.store({
        sigla,
        descricao,
        is_ultimo: isUltimo,
        cor: cor || null,
        legenda: legenda || null,
        exibe_lista_pedido: exibeListaPedido,
        peso: peso === '' ? null : Number(peso),
      });

      if (res.status === HttpStatus.OK || res.status === HttpStatus.CREATED) {
        frontendNotification({
          message: t('successfullyCreated'),
          type: 'success',
        });

        closeModal();
        reqGetAllOrderStatuses();
      } else {
        throw res;
      }
    } catch (err) {
      if (!mobileOrderStatusApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }

    return () => {
      source.cancel('Component got unmounted');
    };
  };

  return (
    <Modal
      title={t('createOrderStatus')}
      showModal
      closeModal={closeModal}
      action={() => reqCreateOrderStatus()}
      isLoading={isLoading}
    >
      <div className="form">
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusSigla">{t('sigla') as string}</label>
            <input
              id="mobileOrderStatusSigla"
              type="text"
              maxLength={10}
              value={sigla}
              onChange={(e) => setSigla(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusDescription">{t('description') as string}</label>
            <input
              id="mobileOrderStatusDescription"
              type="text"
              maxLength={100}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusFinal">{t('isFinalStatus') as string}</label>
            <select
              id="mobileOrderStatusFinal"
              className="select"
              value={isUltimo}
              onChange={(e) => setIsUltimo(e.target.value as 's' | 'n')}
              disabled={isLoading}
            >
              <option value="n">{t('no')}</option>
              <option value="s">{t('yes')}</option>
            </select>
          </div>
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusVisible">{t('showInOrderList') as string}</label>
            <select
              id="mobileOrderStatusVisible"
              className="select"
              value={exibeListaPedido}
              onChange={(e) => setExibeListaPedido(e.target.value as 's' | 'n')}
              disabled={isLoading}
            >
              <option value="n">{t('no')}</option>
              <option value="s">{t('yes')}</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusColor">{t('statusColor') as string}</label>
            <input
              id="mobileOrderStatusColor"
              type="text"
              maxLength={100}
              value={cor}
              onChange={(e) => setCor(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusLegend">{t('statusLegend') as string}</label>
            <input
              id="mobileOrderStatusLegend"
              type="text"
              maxLength={200}
              value={legenda}
              onChange={(e) => setLegenda(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="mobileOrderStatusWeight">{t('weight') as string}</label>
            <input
              id="mobileOrderStatusWeight"
              type="number"
              step="1"
              value={peso}
              onChange={(e) => setPeso(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateOrderStatus;
